import gql from 'graphql-tag';

const GET_INVOICE_LIST = gql`
  query getInvoiceList(
    $listInfo: ListInfoInput
    $clientCustomFields: [String!]
  ) {
    getInvoiceList(listInfo: $listInfo) {
      invoices {
        invoiceId
        displayId
        client {
          accountId
          name
          customFields(fields: $clientCustomFields)
        }
        site {
          name
          id
        }
        customFields
        invoiceDate
        dueDate
        amountDue
        partiallyPaid
        totalAmount
        statusEnum
        sentToClient
        sendEmailError
        emailOperationalStatus
        pushedToIntegratedAppStatus
        pushedToIntegratedAppError
        pushPaymentToIntegratedAppStatus
        pushPaymentToIntegratedAppError

        invoicePaymentTerm {
          paymentTermId
          paymentTermName
          paymentTermValue
          placeholder
          systemPaymentTerm
        }

        paymentMethod {
          paymentMethodId
          paymentMethodName
        }

        paymentDate
        paymentReference
        paymentLinkToken
        paymentLink
      }
      listInfo {
        hasMore
        page
        pageSize
        totalCount
        sort {
          attribute
          order
        }
        condition
      }
    }
  }
`;

const GET_CLIENT_INVOICE = gql`
  query getInvoice(
    $invoiceId: ID!
    $isIntegratedInvoiceId: Boolean!
    $isTaxSplitEnabled: Boolean!
    $clientCustomFields: [String!]
  ) {
    getInvoice(invoiceId: $invoiceId) {
      partiallyPaid
      amountDue
      amountPaid
      transactions {
        transactionId
        mappings {
          mappingId
          paymentSyncStatus
          errorDetails
        }
      }
      invoiceId
      integratedAppInvoiceId @include(if: $isIntegratedInvoiceId)
      displayId
      client {
        accountId
        name
        logoFileName
        customFields(fields: $clientCustomFields)
      }
      billTo {
        id
        name
        line1
        line2
        line3
        countryCode
        stateCode
        city
        postalCode
      }
      shipTo {
        id
        name
        line1
        line2
        line3
        countryCode
        stateCode
        city
        postalCode
      }
      customFields
      senderAddress
      billingAddress
      shippingAddress
      title
      footer
      memo
      invoiceDate
      dueDate
      discountAmount
      taxes {
        tax {
          taxId
          name
        }
        taxRate
        taxableAmount
        taxAmount
        rate @include(if: $isTaxSplitEnabled) {
          rateId
          name
          rateValue
        }
      }
      totalAmount
      statusEnum
      operationStatus
      sentToClient
      sendEmailError
      emailOperationalStatus
      pushedToIntegratedAppStatus
      pushedToIntegratedAppError
      pushPaymentToIntegratedAppStatus
      pushPaymentToIntegratedAppError
      additionalDiscount
      additionalDiscountRate
      additionalDiscountType
      items {
        itemId
        itemOrder
        discountType
        discountAmount
        serviceItem {
          itemId
          name
          invoiceDescription
          quantityType
          roundUpValue
          unitPrice
          category {
            categoryId
            name
          }
          businessHoursUnitPrice
          afterHoursUnitPrice
          useAsWorklogItem
          quantity
        }
        serviceCatalogItem {
          itemId
          name
          itemType
          category {
            categoryId
            name
          }
          offerAsWorklogItem
          # sellingPrice {
          #   details {
          #     value
          #     afterHoursValue
          #   }
          # }
          serviceTypeItem {
            itemId
            offeringType
            hourlyServiceTypeItem {
              itemId
              roundUpValue
              defaultItem
            }
            redeemableServiceBundle {
              itemId
              redeemableQuantity
              applicableHours
            }
          }
        }
        discountRate
        billedDate
        details
        quantity
        unitPrice
        amount
        taxable
        tax {
          taxId
          name
          rates {
            rateId
            name
            rateValue
          }
        }
        offeredItem {
          type
          workItem {
            workId
            module
          }
          itemId
          site {
            id
            name
          }
        }
      }
      conversationIds
      conversationInfo {
        conversationId
        viewedByClient
      }
      notes
      quote {
        quoteId
        displayId
      }
      work {
        workId
        module
        displayId
        project {
          projectId
          displayId
        }
      }
      invoicePaymentTerm {
        paymentTermId
        paymentTermName
        paymentTermValue
        placeholder
        systemPaymentTerm
      }
      paymentMethod {
        paymentMethodId
        paymentMethodName
      }
      paymentDate
      paymentReference
      paymentLinkToken
      paymentLink
    }
  }
`;

const GET_OFFERED_ITEMS_LIST = gql`
  query getOfferedItems($listInfo: ListInfoInput, $isBillingQueue: Boolean!) {
    getOfferedItems(listInfo: $listInfo) {
      items {
        itemId
        contract {
          contractId
          contract {
            contractId
            name
          }
        }
        serviceItem {
          itemId
          name
          invoiceDescription
          quantityType
          roundUpValue
          unitPrice
          category {
            categoryId
            name
          }
          businessHoursUnitPrice
          afterHoursUnitPrice
          useAsWorklogItem
          quantity
        }
        serviceCatalogItem {
          itemId
          name
          itemType
          description
          serviceTypeItem {
            itemId
            offeringType
          }
        }
        status
        unitPrice
        qty
        amount
        revisedQuantity
        revisedAmount
        revisedUnitPrice
        discountRate
        revisedDiscountRate
        adjustedBlockMoney
        adjustedBlockHour
        eligibleBlockMoney
        eligibleBlockHour
        afterHours
        operationStatus
        billDate @include(if: $isBillingQueue)
        client @include(if: $isBillingQueue) {
          accountId
          name
        }
        site @include(if: $isBillingQueue) {
          id
          name
        }
        technician @include(if: $isBillingQueue) {
          userId
          firstName
          lastName
          email
          name
        }
        notes
        ticket {
          displayId
          ticketId
          # contractTicketMapping {
          #   contractId
          #   contract {
          #     contractId
          #   }
          #   inUse
          # }
        }
        project {
          title
          projectId
          displayId
          contract {
            contractId
          }
        }
        workItem {
          workId
          module
          displayId
        }
        worklog {
          worklogId
          technician {
            userId
            name
            firstName
            lastName
          }
          notes
          createdTime
          worklogDate
          ticket {
            displayId
            ticketId
          }
          project {
            title
            projectId
            displayId
          }
          client {
            accountId
            name
          }
        }
      }
      listInfo {
        hasMore
        page
        pageSize
        totalCount
        sort {
          attribute
          order
        }
        condition
      }
    }
  }
`;

const GET_TICKET = gql`
  query getTicket($id: ID!) {
    getTicket(id: $id) {
      ticketId
      contractTicketMapping {
        contractId
        contract {
          contractId
          name
        }
      }
    }
  }
`;

const GET_OFFERED_ITEMS_LISTMETA = gql`
  query getOfferedItemsListMeta {
    getOfferedItemsListMeta
  }
`;

const GET_SEND_AND_INTEGRATION_STATUS = gql`
  query getInvoice($invoiceId: ID!) {
    getInvoice(invoiceId: $invoiceId) {
      invoiceId
      statusEnum
      operationStatus
      sentToClient
      sendEmailError
      emailOperationalStatus
      pushedToIntegratedAppStatus
      pushedToIntegratedAppError
      pushPaymentToIntegratedAppStatus
      pushPaymentToIntegratedAppError
      conversationIds
      conversationInfo {
        conversationId
        viewedByClient
      }
    }
  }
`;

const GET_CONDITIONS_META_FOR_INVOICE = gql`
  query {
    getInvoiceListViewMeta
  }
`;

const CONSTRUCT_INVOICE_ITEMS = gql`
  query constructInvoiceItems(
    $constructInvoiceItems: [OfferedItemIdentifierInput]!
  ) {
    constructInvoiceItems(input: $constructInvoiceItems) {
      site {
        id
        name
      }
      billedDate
      serviceItem {
        itemId
        name
        invoiceDescription
        quantityType
        roundUpValue
        unitPrice
        category {
          categoryId
          name
        }
        businessHoursUnitPrice
        afterHoursUnitPrice
        useAsWorklogItem
        quantity
      }
      serviceCatalogItem {
        itemId
        name
        itemType
        category {
          categoryId
          name
        }
        offerAsWorklogItem
        # sellingPrice {
        #   details {
        #     value
        #     afterHoursValue
        #   }
        # }
        serviceTypeItem {
          itemId
          offeringType
          hourlyServiceTypeItem {
            itemId
            roundUpValue
            defaultItem
          }
          redeemableServiceBundle {
            itemId
            redeemableQuantity
            applicableHours
          }
        }
      }
      details
      quantity
      unitPrice
      discountRate
      amount
      taxable
      tax {
        taxId
        name
        rates {
          rateId
          name
          rateValue
        }
      }
      offeredItem {
        type
        workItem {
          module
        }
        itemId
        serviceItem {
          itemId
          name
          invoiceDescription
          quantityType
          roundUpValue
          unitPrice
          category {
            categoryId
            name
          }
          businessHoursUnitPrice
          afterHoursUnitPrice
          useAsWorklogItem
          quantity
        }
        status
        unitPrice
        qty
        discountRate
        amount
        revisedUnitPrice
        revisedQuantity
        revisedDiscountRate
        revisedAmount
      }
    }
  }
`;

const GET_INVOICE_PAYMENT_METHODS = gql`
  query getPaymentMethods {
    getPaymentMethods {
      paymentMethodId
      paymentMethodName
      # systemMethod
    }
  }
`;

const GET_INVOICE_PAYMENT_TERMS = gql`
  query getPaymentTerms {
    getPaymentTerms {
      paymentTermId
      paymentTermName
      paymentTermValue
      systemPaymentTerm
      placeholder
    }
  }
`;

const GET_CONTENT_FOR_SEND_INVOICE = gql`
  query getContentForSendInvoice($input: InvoiceIdentifierInput!) {
    getContentForSendInvoice(input: $input) {
      conversationId
      content
      subject
      attachments {
        fileName
        originalFileName
        fileSize
      }
      toUsers {
        user {
          userId
          email
          name
        }
      }
      ccUsers {
        user {
          userId
          email
          name
        }
      }
      bccUsers {
        user {
          userId
          email
          name
        }
      }
    }
  }
`;

const CREATE_INVOICE_WITH_CONVERSATION = gql`
  mutation sendInvoice($input: InvoiceConversationInput!) {
    sendInvoice(input: $input)
  }
`;

const GET_INVOICE_AUDIT_ADVANCED_FILTER_META = gql`
  query($input: String!) {
    getOfferedItemListMeta(input: $input)
  }
`;

const GET_WORKLOG_MODULE = gql`
  query getWorklogModules {
    getWorklogModules
  }
`;

const GET_OFFERED_ITEMS_MODULE = gql`
  query getOfferedItemModules {
    getOfferedItemModules
  }
`;

const GET_INVOICE_CONFIG = gql`
  query getInvoiceConfiguration($input: ClientIdentifierInput) {
    getInvoiceConfiguration(input: $input) {
      layoutConfiguration
      preferences
    }
  }
`;

const GET_INVOICE_LIST_VIEW_META = gql`
  query getInvoiceListViewMeta($input: InvoiceMetaSource!) {
    getInvoiceListViewMeta(input: $input)
  }
`;

const GET_INVOICE_STATUS = gql`
  query {
    getInvoiceStatus
  }
`;

const GET_THIRDPARTY_INTEGRATED_APP_STATUS = gql`
  query {
    getThirdPartyIntegratedAppStatus
  }
`;

const FETCH_INVOICE_CONFIGURATION = gql`
  query fetchInvoiceConfiguration($input: ClientIdentifierInput) {
    fetchInvoiceConfiguration(input: $input) {
      reminderEnabled
      reminders {
        reminderId
        reminderConfig {
          triggerType
          offsetValue
          offsetUnit
          referenceDateType
        }
        reminderTemplate {
          id
          name
          subject
          content
          placeholdersUsed
          to {
            type
            email
            group {
              groupId
              name
              description
            }
            user {
              userId
              email
              name
              firstName
              lastName
            }
            userTypeRole {
              roleId
              name
            }
            placeHolder
          }
          cc {
            type
            user {
              userId
              email
              firstName
              lastName
              name
            }
            placeHolder
            email
          }
          bcc {
            type
            user {
              userId
              email
              firstName
              lastName
              name
            }
            placeHolder
            email
          }
          activated
          templateSubtype
          attachmentList {
            fileName
            originalFileName
            fileSize
          }
        }
      }
      configurationId
      autoSyncWithTP
      enableSiteSpecificInvoice
      generateEmptyInvoice
      autoSend
      attachPdf
      attachUsagePdf
      autoApplyCredits
      invoiceSchedulesAllowed
      invoicePaymentTerm {
        paymentTermId
        paymentTermName
        paymentTermValue
        placeholder
        systemPaymentTerm
      }
      invoicePaymentMethod {
        paymentMethodId
        paymentMethodName
      }
      invoiceLineItemTemplates {
        templateId
        itemType
        content
        placeholdersUsed
      }
      invoiceXmlFormat
      invoiceXmlEnabled
      taxable
      salesTax {
        taxId
        name
        rates {
          rateId
          name
          rateValue
        }
      }
      invoiceTemplate {
        templateId
        memo
        title
        footer
        defaultTemplate
        layoutConfiguration
        preferences
      }
      quoteTemplate {
        templateId
        memo
        title
        footer
        layoutConfiguration
        preferences
      }
      lateFee {
        lateFeeItem {
          itemId
          name
        }
        feeType
        value {
          model
          details {
            value
            afterHoursValue
          }
        }
        frequency
        gracePeriod
        taxable
        lateFeeEnabled
      }
      invoiceEmailTemplate {
        id
        name
        subject
        content
        placeholdersUsed
        to {
          type
          email
          group {
            groupId
            name
            description
          }
          user {
            userId
            email
            name
            firstName
            lastName
          }
          userTypeRole {
            roleId
            name
          }
          placeHolder
        }
        cc {
          type
          user {
            userId
            email
            firstName
            lastName
            name
          }
          placeHolder
          email
        }
        bcc {
          type
          user {
            userId
            email
            firstName
            lastName
            name
          }
          placeHolder
          email
        }
        activated
        templateSubtype
        attachmentList {
          fileName
          originalFileName
          fileSize
        }
      }
      overriddenSettings
    }
  }
`;

export const GET_INVOICE_USAGE_DATA = gql`
  query getInvoiceUsageData($invoiceId: ID!) {
    getInvoiceUsageData(invoiceId: $invoiceId)
  }
`;

export {
  GET_TICKET,
  GET_INVOICE_LIST_VIEW_META,
  GET_INVOICE_STATUS,
  GET_THIRDPARTY_INTEGRATED_APP_STATUS,
  GET_INVOICE_LIST,
  GET_CLIENT_INVOICE,
  GET_OFFERED_ITEMS_LISTMETA,
  GET_OFFERED_ITEMS_LIST,
  GET_SEND_AND_INTEGRATION_STATUS,
  GET_CONDITIONS_META_FOR_INVOICE,
  CONSTRUCT_INVOICE_ITEMS,
  GET_INVOICE_PAYMENT_METHODS,
  GET_INVOICE_PAYMENT_TERMS,
  GET_CONTENT_FOR_SEND_INVOICE,
  CREATE_INVOICE_WITH_CONVERSATION,
  GET_INVOICE_AUDIT_ADVANCED_FILTER_META,
  GET_WORKLOG_MODULE,
  GET_OFFERED_ITEMS_MODULE,
  GET_INVOICE_CONFIG,
  FETCH_INVOICE_CONFIGURATION
};
